import { Component, Input, OnInit, Output, EventEmitter, HostListener } from '@angular/core';
import { UserSmall } from '@interfaces/user';
import { Journey, ColorType, FindJourneyDto } from '@interfaces/rest/interfaces/journey';
import { Table } from '@interfaces/others';
import { NavController, ModalController } from '@ionic/angular';
import { DpUtilsService } from 'digitup-lib/dist/digitup-lib';
import { RestService } from '@services/rest.service';
import { AlertConfirmComponent } from '@components/alert-confirm/alert-confirm.component';
import { SipService } from '@services/sip.service';
import { ServicesModalComponent } from './services-modal.component';

export interface CallUserData {
  user?: UserSmall;
  journeys: Journey[];
}

@Component({
  selector: 'app-call-card',
  templateUrl: './call-card.component.html',
  styleUrls: ['./call-card.component.scss'],
})
export class CallCardComponent implements OnInit {
  @Input() type: 'ringing' | 'inCall' | 'onHold' = 'ringing';
  @Input() callerName = ''; // Mantener por compatibilidad, pero se usará userData si está disponible
  @Input() callId = '';
  @Input() callerId = ''; // Número de teléfono
  @Input() userData?: CallUserData; // 🔥 Objeto completo de datos de usuario

  @Output() answer = new EventEmitter<void>();
  @Output() hangup = new EventEmitter<void>();
  @Output() hold = new EventEmitter<void>();
  @Output() resume = new EventEmitter<void>();

  // 🔥 Modal de servicios
  showServicesModal = false;

  // 🔥 Columnas para la tabla de servicios
  servicesColumns: Table[] = [
    // {
    //   field: 'id',
    //   name: 'id',
    //   width: 10
    // },
    {
      field: 'from',
      name: 'from',
      width: 25,
      class: 'align_left'
    },
    {
      field: 'to',
      name: 'to',
      width: 25,
      class: 'align_left'
    },
    {
      field: 'createdAt',
      name: 'date',
      width: 20,
      type: 'datetime'
    },
    {
      field: 'statusObj',
      name: 'status',
      width: 10,
      type: 'multiValColors'
    },
    {
      field: 'action',
      name: '',
      width: 20,
      type: 'actions',
      actions: [
        {
          type: 'view'
        },
        {
          type: 'duplicate'
          // 🔥 Duplicar siempre está habilitado
        },
        {
          type: 'delete',
          disabledField: 'canEdit' // 🔥 Deshabilitar si canEdit es false (solo para cancelar)
        }
      ]
    }
  ];

  constructor(
    private nav: NavController,
    private utils: DpUtilsService,
    private rest: RestService,
    private modalController: ModalController,
    private sip: SipService
  ) { }

  ngOnInit() { }

  /**
   * 🔥 Obtiene el color según el estado del journey
   */
  private getStatusColor(status?: string): ColorType {
    if (!status) {
      return 'white';
    }

    // Estados rojos (cancelados/cerrados/error)
    if (['CLOSED', 'CANCELED_BY_USER', 'CANCELED_BY_DRIVER', 'CANCELED_BY_MANAGER', 'CANCEL', 'ERROR', 'EXPIRED'].includes(status)) {
      return 'red';
    }

    // Estados verdes (completados/confirmados)
    if (['TRIP_COMPLETED', 'DRIVER_CONFIRMED'].includes(status)) {
      return 'green';
    }

    // Estados azules (reservas/activos)
    if (['RECURRENT_RESERVE_NEW', 'RESERVE_WAITING', 'TRIP_STARTED'].includes(status)) {
      return 'blue';
    }

    // Por defecto blanco
    return 'white';
  }

  /**
   * 🔥 Verifica si un journey está en estado activo (permite editar/duplicar/cancelar)
   */
  private isJourneyActive(journey: Journey): boolean {
    return journey.status === 'NEW' ||
      journey.status === 'DRIVER_SEARCHING' ||
      journey.status === 'DRIVER_FOUND' ||
      journey.status === 'DRIVER_CONFIRMED' ||
      journey.status === 'DRIVER_ARRIVED' ||
      journey.status === 'TRIP_STARTED';
  }

  /**
   * 🔥 Transforma los journeys para la tabla, creando campos from y to y statusObj
   */
  getJourneysForTable(): any[] {
    if (!this.userData?.journeys) {
      return [];
    }

    return this.userData.journeys.map(journey => {
      // Determinar color según el estado
      const color = this.getStatusColor(journey.status);

      // Crear objeto transformado manteniendo el journey original
      const transformed: any = {
        ...journey,
        // Crear campo 'from' desde fromAddress
        from: journey.fromAddress
          ? `${journey.fromAddress.title || ''} - ${journey.fromAddress.description || ''}`.trim()
          : '',
        // Crear campo 'to' desde toAddress
        to: journey.toAddress
          ? `${journey.toAddress.title || ''} - ${journey.toAddress.description || ''}`.trim()
          : '',
        // Crear statusObj en formato correcto para multiValColors
        statusObj: journey.status ? [{
          name: journey.status,
          translatePath: 'services',
          color: color
        }] : [],
        // 🔥 Campo para deshabilitar acciones cuando el journey no está activo
        canEdit: this.isJourneyActive(journey)
      };

      return transformed;
    });
  }

  /**
   * 🔥 Obtiene el nombre del caller de forma prioritaria desde userData
   */
  getDisplayName(): string {
    if (this.userData?.user?.name) {
      return this.userData.user.name;
    }
    return this.callerName || this.callerId || 'Número desconocido';
  }

  /**
   * 🔥 Obtiene el número de servicios activos
   */
  getActiveServicesCount(): number {
    if (!this.userData?.journeys) {
      return 0;
    }
    return this.userData.journeys.filter(journey =>
      journey.status === 'NEW' ||
      journey.status === 'DRIVER_SEARCHING' ||
      journey.status === 'DRIVER_FOUND' ||
      journey.status === 'DRIVER_CONFIRMED' ||
      journey.status === 'DRIVER_ARRIVED' ||
      journey.status === 'TRIP_STARTED'
    ).length;
  }

  /**
   * 🔥 Verifica si hay servicios activos
   */
  hasActiveServices(): boolean {
    return this.getActiveServicesCount() > 0;
  }

  /**
   * 🔥 Verifica si hay journeys para mostrar el botón de listado
   */
  hasJourneys(): boolean {
    return !!(this.userData?.journeys && this.userData.journeys.length > 0);
  }

  /**
   * 🔥 Obtiene el texto del botón principal según el estado
   */
  getMainButtonText(): string {
    switch (this.type) {
      case 'ringing':
        return 'Aceptar';
      case 'inCall':
        return 'Pausar';
      case 'onHold':
        return 'Retomar';
      default:
        return '';
    }
  }

  /**
   * 🔥 Obtiene el icono del botón principal según el estado
   */
  getMainButtonIcon(): string {
    switch (this.type) {
      case 'ringing':
        return 'dp-incoming';
      case 'inCall':
        return 'pause';
      case 'onHold':
        return 'play';
      default:
        return '';
    }
  }

  /**
   * 🔥 Maneja el click del botón principal
   */
  onMainButtonClick() {
    switch (this.type) {
      case 'ringing':
        this.onAnswer();
        break;
      case 'inCall':
        this.onHold();
        break;
      case 'onHold':
        this.onResume();
        break;
    }
  }

  onAnswer() {
    this.answer.emit();
  }

  onHangup() {
    this.hangup.emit();
  }

  onHold() {
    this.hold.emit();
  }

  onResume() {
    this.resume.emit();
  }

  onShowList() {
    this.openServicesModal();
  }

  /**
   * 🔥 Abre el modal de servicios usando ModalController
   */
  async openServicesModal() {
    const modal = await this.modalController.create({
      component: ServicesModalComponent,
      cssClass: 'services-modal',
      componentProps: {
        columns: this.servicesColumns,
        journeys: this.getJourneysForTable(),
        displayName: this.getDisplayName(),
        callerId: this.callerId, // 🔥 Pasar callerId para que el modal pueda suscribirse
        onAction: (ev: any) => this.doActionParent(ev)
      }
    });

    await modal.present();

    const { data } = await modal.onWillDismiss();
    // Si necesitamos hacer algo cuando se cierra el modal
  }

  closeServicesModal() {
    // Ya no se usa, pero mantenemos por compatibilidad
  }

  /**
   * 🔥 Maneja las acciones de la tabla de servicios
   */
  async doActionParent(ev: any) {
    console.log('doActionParent', ev);
    // El objeto ev.line es el journey transformado, pero conserva todas las propiedades originales
    const journey = ev.line as Journey;

    switch (ev.action) {
      case 'view':
        this.nav.navigateForward(`services/service-detail/${journey.id}`);
        break;
      case 'duplicate':
        await this.confirmDuplicate(journey);
        break;
      case 'delete':
        await this.confirmDelete(journey);
        break;
    }
  }

  /**
   * 🔥 Confirma si se quiere duplicar el servicio
   */
  async confirmDuplicate(journey: Journey) {
    const result = await this.utils.showAlert({
      header: 'Duplicar servicio',
      message: `¿Estás seguro de que quieres duplicar el servicio #${journey.id}?`,
      cancel: true,
      wait: true,
      cssClass: 'services-modal-confirm',
      buttonOk: 'Duplicar',
      buttonCancel: 'Cancelar'
    });

    if (result) {
      await this.duplicateService(journey);
    }
  }

  /**
   * 🔥 Confirma si se quiere eliminar el servicio usando AlertConfirmComponent
   */
  async confirmDelete(journey: Journey) {
    const result = await this.utils.showAlert({
      header: 'Cancelar servicio',
      message: `¿Estás seguro de que quieres cancelar el servicio #${journey.id}?`,
      cancel: true,
      wait: true,
      cssClass: 'services-modal-confirm',
      buttonOk: 'Cancelar servicio',
      buttonCancel: 'No cancelar'
    });

    if (result) {
      await this.deleteService(journey);
    }
  }

  /**
   * 🔥 Duplica el servicio
   */
  async duplicateService(journey: Journey) {
    if (!journey.id) {
      await this.utils.showToast({
        message: 'Error: No se pudo obtener el ID del servicio',
        cssClass: 'danger'
      });
      return;
    }

    try {
      // Crear FindJourneyDto desde el journey existente
      const dtoToDuplicate: FindJourneyDto = {
        fromLat: journey.fromLat,
        fromLng: journey.fromLng,
        fromAddress: journey.fromAddress || {
          title: '',
          description: ''
        },
        toLat: journey.toLat,
        toLng: journey.toLng,
        toAddress: journey.toAddress || {
          title: '',
          description: ''
        },
        persons: journey.persons || 1,
        extra: journey.extra || [],
        note: journey.note || '',
        reserve: journey.reserve || false,
        priority: journey.priority || false
      };

      // Si hay reserveDate, agregarlo
      if (journey.reserve && journey.reserveDate) {
        dtoToDuplicate.reserveDate = journey.reserveDate;
      }

      // Si hay userId, agregarlo
      if (journey.userId) {
        dtoToDuplicate.toAnotherUser = journey.userId;
      }

      await this.utils.showLoading({ duration: 20000 });
      const result = await this.rest.findJourney(dtoToDuplicate);
      await this.utils.hideLoading();

      if (result.ok) {
        await this.utils.showToast({
          message: 'Servicio duplicado correctamente',
          cssClass: 'success'
        });

        // 🔥 Refrescar los datos del usuario para actualizar la lista
        if (this.callerId) {
          await this.sip.refreshUserData(this.callerId);
        }
        // El modal se actualizará automáticamente gracias a la suscripción en ServicesModalComponent
      } else {
        await this.utils.showToast({
          message: result.message || 'Error al duplicar el servicio',
          cssClass: 'danger'
        });
      }
    } catch (error) {
      await this.utils.hideLoading();
      console.error('Error al duplicar servicio:', error);
      await this.utils.showToast({
        message: 'Error al duplicar el servicio',
        cssClass: 'danger'
      });
    }
  }

  /**
   * 🔥 Elimina el servicio (cancela el journey)
   */
  async deleteService(journey: Journey) {
    if (!journey.id) {
      await this.utils.showToast({
        message: 'Error: No se pudo obtener el ID del servicio',
        cssClass: 'danger'
      });
      return;
    }

    try {
      await this.utils.showLoading();
      const resultAction = await this.rest.cancelTotalJourney(journey.id, 'AFTER_ACCEPT');
      await this.utils.hideLoading();

      if (resultAction.ok) {
        await this.utils.showToast({
          message: 'Servicio cancelado',
          cssClass: 'success'
        });

        // 🔥 Refrescar los datos del usuario para actualizar la lista
        if (this.callerId) {
          await this.sip.refreshUserData(this.callerId);
        }
        // El modal se actualizará automáticamente gracias a la suscripción en ServicesModalComponent
      } else {
        await this.utils.showToast({
          message: resultAction.message || 'Error al cancelar el servicio',
          cssClass: 'danger'
        });
      }
    } catch (error) {
      await this.utils.hideLoading();
      console.error('Error al cancelar servicio:', error);
      await this.utils.showToast({
        message: 'Error al cancelar el servicio',
        cssClass: 'danger'
      });
    }
  }

  // @HostListener('window:keydown', ['$event'])
  // handleKeyboardEvent(event: KeyboardEvent) {
  //   // Solo procesar teclas si el componente es visible
  //   if (!this.isComponentVisible()) {
  //     return;
  //   }

  //   // ⚠️ IMPORTANTE: Ignorar atajos de teclado cuando el usuario está escribiendo
  //   // Esto previene que se disparen acciones mientras se escribe en inputs/textareas
  //   if (this.isUserTyping(event)) {
  //     return;
  //   }

  //   switch (event.key.toLowerCase()) {
  //     case 'enter':
  //       // Enter solo funciona para llamadas entrantes (ringing)
  //       if (this.type === 'ringing') {
  //         event.preventDefault();
  //         this.onAnswer();
  //       }
  //       break;
  //     case 'c':
  //       // C funciona para colgar en ambos estados
  //       event.preventDefault();
  //       this.onHangup();
  //       break;
  //   }
  // }

  // /**
  //  * Verifica si el usuario está escribiendo en un campo de entrada
  //  * @param event Evento del teclado
  //  * @returns true si el usuario está en un input, textarea o elemento editable
  //  */
  // private isUserTyping(event: KeyboardEvent): boolean {
  //   const target = event.target as HTMLElement;
  //   const tagName = target.tagName.toLowerCase();

  //   // Verificar si es un campo de entrada estándar
  //   if (tagName === 'input' || tagName === 'textarea') {
  //     return true;
  //   }

  //   // Verificar si es un elemento contenteditable
  //   if (target.isContentEditable) {
  //     return true;
  //   }

  //   // Verificar si está dentro de un elemento con role="textbox"
  //   if (target.getAttribute('role') === 'textbox') {
  //     return true;
  //   }

  //   return false;
  // }

  // private isComponentVisible(): boolean {
  //   // El componente es visible si tiene un tipo válido
  //   return this.type === 'ringing' || this.type === 'inCall' || this.type === 'onHold';
  // }

}
